import { useState, useRef } from "react";
import apiService, { ApiError } from "../services/api";
import { getClientIdFromUrl, getUserIdFromUrl } from "../utils/urlParams";

export const useConversations = () => {
  const [conversations, setConversations] = useState([]);
  const [currentConversation, setCurrentConversation] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  // New state for scene-by-scene workflow
  const [projectData, setProjectData] = useState(null);
  const [currentSceneIndex, setCurrentSceneIndex] = useState(0);
  const [sceneGenerationStatus, setSceneGenerationStatus] = useState({});
  const [isGeneratingScene, setIsGeneratingScene] = useState(false);
  const [originalPrompt, setOriginalPrompt] = useState("");
  const [savedAspectRatio, setSavedAspectRatio] = useState(null);
  const [savedSceneDuration, setSavedSceneDuration] = useState(null);
  const [savedLogoUrl, setSavedLogoUrl] = useState(null);
  const [savedReferenceImageUrl, setSavedReferenceImageUrl] = useState(null);
  const [savedReferenceVideoUrl, setSavedReferenceVideoUrl] = useState(null);
  const [savedLanguage, setSavedLanguage] = useState(null);
  const [savedProductImgUrls, setSavedProductImgUrls] = useState([]);
const [savedModelImgUrls, setSavedModelImgUrls] = useState([]);
const [savedBackgroundUrls, setSavedBackgroundUrls] = useState([]);
  const [savedVoiceSampleUrl, setSavedVoiceSampleUrl] = useState(null);
  const [savedAiModel, setSavedAiModel] = useState('veo');
  const savedAiModelRef = useRef('veo');
  const [savedPersonalPrompt, setSavedPersonalPrompt] = useState(false);
  const [savedGenerateThumbnails, setSavedGenerateThumbnails] = useState(false);
  const [savedHookLine, setSavedHookLine] = useState(null);
  const [resetKey, setResetKey] = useState(0);

  const handleSendMessage = async (
    message,
    filePath = null,
    imagePreview = null,
    chips = [],
    totalDuration = null,
    personalPrompt = false,
    dialogue = true,
    presenter = null,
    aspectRatio = "16:9",
    offers = null,
    logo = null,
    logoUrl = null,
    referenceImageUrl = null,
    referenceVideoUrl = null,
    language = null,
    companyName = null,
    productImgUrlsArg = null,
    modelImgUrlsArg = null,
    backgroundUrlsArg = null,
    onProcessingComplete = null,
    aiMode = 'veo',
    voiceSampleUrl = null,
    referenceImageUrlArg = null,
    generateThumbnails = false,
    hookLine = null,
  ) => {
    const newMessage = {
      text: message,
      type: "user",
      image: imagePreview,
    };
    setIsLoading(true);

    // Add user's message first
    const updatedConversations = [...conversations];
    if (updatedConversations[currentConversation]) {
      updatedConversations[currentConversation].messages.push(newMessage);
    } else {
      updatedConversations.push({
        title: message.slice(0, 30) + "...",
        messages: [newMessage],
      });
    }
    setConversations(updatedConversations);

    try {
      // Prepare request body with enhanced prompt structure
      let fullPrompt = message;

      if (chips.length > 0) {
        // Group chips by category for better organization
        const videoType = chips.find((chip) => chip.category === "Video Type");
        const presenter = chips.find((chip) => chip.category === "Presenter");
        const familyOrigin = chips.find(
          (chip) => chip.category === "Family Origin",
        );
        const styleTone = chips.find(
          (chip) => chip.category === "Style & Tone",
        );
        const background = chips.find((chip) => chip.category === "Background");
        const seasonWeather = chips.find(
          (chip) => chip.category === "Season/Weather",
        );
        const cameraShot = chips.find(
          (chip) => chip.category === "Camera Shot",
        );
        const cameraMotion = chips.find(
          (chip) => chip.category === "Camera Motion",
        );
        const cta = chips.find((chip) => chip.category === "CTA");
        const industry = chips.find((chip) => chip.category === "Industry");
        const website = chips.find((chip) => chip.category === "Website");
        const branding = chips.find((chip) => chip.category === "Branding");
        const textPrompt = chips.find(
          (chip) => chip.category === "Text Prompt",
        );
        const offersChip = chips.find((chip) => chip.category === "Offers");
        const logoChip = chips.find((chip) => chip.category === "Logo");
        const languageChip = chips.find((chip) => chip.category === "Language");
        const companyNameChip = chips.find(
          (chip) => chip.category === "Company Name",
        );

        // Build structured prompt
        let structuredPrompt = message;
        let specifications = [];

        if (videoType) specifications.push(`Video Type: ${videoType.value}`);
        if (presenter) specifications.push(`Presenter: ${presenter.value}`);
        if (familyOrigin)
          specifications.push(`Family Origin: ${familyOrigin.value}`);
        if (styleTone) specifications.push(`Style & Tone: ${styleTone.value}`);
        if (background) specifications.push(`Background: ${background.value}`);
        if (seasonWeather)
          specifications.push(`Season/Weather: ${seasonWeather.value}`);
        if (cameraShot) specifications.push(`Camera Shot: ${cameraShot.value}`);
        if (cameraMotion)
          specifications.push(`Camera Motion: ${cameraMotion.value}`);
        if (cta) specifications.push(`Call-to-Action: ${cta.value}`);
        if (industry) specifications.push(`Industry: ${industry.value}`);
        if (website) specifications.push(`Reference Website: ${website.value}`);
        if (branding) {
          if (branding.value === "No") {
            specifications.push(
              `No company branding: Create a generic video without company banners`,
            );
          } else if (branding.value.startsWith("Yes -")) {
            const position = branding.value.replace("Yes - ", "");
            specifications.push(
              `Add company banner/logo in the ${position.toLowerCase()} of the video`,
            );
          } else {
            specifications.push(
              `Add company banner in the background of the video`,
            );
          }
        }
        if (offersChip) {
          specifications.push(`Offers: ${offersChip.value}`);
        }
        if (logoChip) {
          specifications.push(`Logo Position: ${logoChip.value}`);
        }
        if (languageChip) {
          specifications.push(`Language: ${languageChip.value}`);
        }
        if (companyNameChip) {
          specifications.push(`Company Name: ${companyNameChip.value}`);
        }
        if (textPrompt)
          specifications.push(`Additional Text Prompt: ${textPrompt.value}`);

        if (specifications.length > 0) {
          structuredPrompt = `${message}\n\nVideo Specifications:\n${specifications.join("\n")}`;
        }

        fullPrompt = structuredPrompt;
      }

      // Check if there's a website chip
      const websiteChip = chips.find((chip) => chip.category === "Website");
     const productImgUrls =
    productImgUrlsArg?.length > 0 ? productImgUrlsArg :
    chips.find((c) => c.category === "Product Image URLs")?.value || [];
    console.log('📦 productImgUrlsArg:', productImgUrlsArg);
console.log('📦 modelImgUrlsArg:', modelImgUrlsArg);
console.log('📦 backgroundUrlsArg:', backgroundUrlsArg);

const modelImgUrls =
    modelImgUrlsArg?.length > 0 ? modelImgUrlsArg :
    chips.find((c) => c.category === "Model Image URLs")?.value || [];

const backgroundUrls =
    backgroundUrlsArg?.length > 0 ? backgroundUrlsArg :
    chips.find((c) => c.category === "Background Image URLs")?.value || [];

      // Call the API service - only send totalDuration and aspectRatio
      // Backend will calculate sceneDuration from totalDuration
      const data = await apiService.generateScript(fullPrompt, {
        website: websiteChip?.value,
        totalDuration,
        imgUrl: filePath,
        personalPrompt,
        dialogue,
        presenter,
        aspectRatio,
        offers,
        logo,
        logoUrl,
        language: Array.isArray(language) ? language : language ? [language] : [],
        companyName,
        productImgUrls,
        modelImgUrls,
        backgroundUrls,
        referenceImageUrl: referenceImageUrlArg || referenceImageUrl,
        referenceVideoUrl,
        aiMode,
      });

      // Extract scenes from the response (handle multiple response formats)
      const scenes =
        data.websiteDetails?.scenes ||
        data.scriptDetails?.scenes ||
        data.scenes ||
        [];

      if (scenes.length > 0) {
        // Prepare scenes for dialogue editing
        const validatedScenes = scenes.map((scene) => {
          const rawText =
            scene.fullText ||
            scene.fulltext ||
            scene.scriptText ||
            scene.scripttext ||
            "";

          // When dialogue is false, use empty string for dialogue
          // Otherwise, extract dialogue from rawText or use dialogue directly
          let sceneDialogue = "";

          if (dialogue) {
            const dialogueMatch =
              rawText.match(/Dialogue:\s*"([^\"]*)"/i) ||
              rawText.match(/Dialogue:\s*([^\n]*)/i);
            sceneDialogue = dialogueMatch
              ? dialogueMatch[1].trim()
              : scene.dialogue || "";
          }

          const visualMatch = rawText.match(/^(.*?)Dialogue:/is);
          const visualDescription = visualMatch
            ? visualMatch[1].trim()
            : scene.visualDescription || scene.visualdescription || rawText;

          const wordCount = sceneDialogue
            .split(/\s+/)
            .filter((w) => w.length > 0).length;

          return {
            sceneNumber: scene.sceneNumber,
            scriptText: rawText,
            dialogue: sceneDialogue,
            visualDescription: visualDescription,
            fullText: rawText,
            wordCount: wordCount,
            isValid: dialogue ? wordCount >= 15 && wordCount <= 16 : true,
          };
        });

        // Store original prompt and video settings for later use
        setOriginalPrompt(fullPrompt);
        setSavedAspectRatio(aspectRatio || data.aspectRatio || null); // ✅ use input first
        setSavedSceneDuration(totalDuration || data.sceneDuration || null); // ✅ use input first
        setSavedLogoUrl(logoUrl || data.logoUrl || null); // ✅ use input first
        setSavedReferenceImageUrl(referenceImageUrl || data.referenceImageUrl || null);
        setSavedReferenceVideoUrl(referenceVideoUrl || data.referenceVideoUrl || null);
        setSavedLanguage(language || data.language || null); // ✅ use input first
        setSavedProductImgUrls(productImgUrls);
setSavedModelImgUrls(modelImgUrls);
setSavedBackgroundUrls(backgroundUrls);
        setSavedVoiceSampleUrl(voiceSampleUrl);
        setSavedAiModel(aiMode || 'veo');
        savedAiModelRef.current = aiMode || 'veo';
        setSavedPersonalPrompt(personalPrompt);
        setSavedGenerateThumbnails(generateThumbnails);
        setSavedHookLine(hookLine);

        // If dialogue is false, skip DialogueEditor and create project directly
        if (!dialogue) {
          try {
            // Get clientId and userId from URL
            const clientId = getClientIdFromUrl();
            const userId = getUserIdFromUrl();

            // Create project directly with scenes as-is
            const projectData = await apiService.createProject(
              validatedScenes,
              fullPrompt,
              clientId,
              userId,
              {
                aspectRatio: aspectRatio || data.aspectRatio || null,
                sceneDuration: totalDuration || data.sceneDuration || null,
                logoUrl: logoUrl || data.logoUrl || null,
                referenceImageUrl,
                referenceVideoUrl,
                language: language ? (Array.isArray(language) ? language[0] : language) : data.language || null,
               productImgUrls,
modelImgUrls,
backgroundUrls,
                voiceSampleUrl,
                isDirectMode: personalPrompt,
                aiMode,
              },
            );

            // Store project data
            setProjectData(projectData);
            setCurrentSceneIndex(0);

            // Add bot response with scene workflow (skip dialogue editor)
            const botResponse = {
              text: "",
              type: "bot",
              isSceneWorkflow: true,
              projectData: projectData,
              aiModel: aiMode,
            };
            setConversations(prev => {
              const updated = prev.map((conv, i) =>
                i === currentConversation
                  ? { ...conv, messages: [...conv.messages, botResponse] }
                  : conv
              );
              return updated;
            });

              await generateScene(projectData.projectId, 1, {
                useSubjectImage: Boolean(
                  referenceImageUrl ||
                  logoUrl ||
                  productImgUrls?.length ||
                  modelImgUrls?.length ||
                  backgroundUrls?.length
                ),
              });
          } catch (error) {
            console.error("Failed to create project:", error);
            const errorResponse = {
              text: `Error: ${error.message || "Failed to create project. Please try again."}`,
              type: "bot",
            };
            setConversations(prev => {
              const updated = prev.map((conv, i) =>
                i === currentConversation
                  ? { ...conv, messages: [...conv.messages, errorResponse] }
                  : conv
              );
              return updated;
            });
          }
        } else {
          // Show dialogue editor for confirmation
          const botResponse = {
            text: "",
            type: "bot",
            isDialogueEditor: true,
            scenes: validatedScenes,
            aiModel: aiMode,
          };
          setConversations(prev => {
            const updated = prev.map((conv, i) =>
              i === currentConversation
                ? { ...conv, messages: [...conv.messages, botResponse] }
                : conv
            );
            return updated;
          });
        }
      } else {
        // Fallback for old format (direct video URL)
        const isVideoUrl = typeof data === "string" && data.startsWith("http");
        const botResponse = {
          text: isVideoUrl ? "Video generated!" : (typeof data === "string" ? data : data?.message || JSON.stringify(data)),
          type: "bot",
          isVideo: isVideoUrl,
          videoUrl: isVideoUrl ? data : null,
        };

        setConversations(prev => {
          const updated = prev.map((conv, i) =>
            i === currentConversation
              ? { ...conv, messages: [...conv.messages, botResponse] }
              : conv
          );
          return updated;
        });
      }

      // Call the callback to hide processing info
      if (onProcessingComplete) {
        onProcessingComplete();
      }
    } catch (error) {
      console.error("API Error:", error);
      const errorResponse = {
        text: `Error: ${error.message || "Failed to generate video. Please try again."}`,
        type: "bot",
      };
      setConversations(prev => {
        const updated = prev.map((conv, i) =>
          i === currentConversation
            ? { ...conv, messages: [...conv.messages, errorResponse] }
            : conv
        );
        return updated;
      });

      // Call the callback to hide processing info even on error
      if (onProcessingComplete) {
        onProcessingComplete();
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Generate a specific scene
  const generateScene = async (projectId, sceneNumber, options = {}) => {
    setIsGeneratingScene(true);
    try {
      let sceneData;
      if (savedAiModelRef.current === 'seedance') {
        sceneData = await apiService.generateSceneSeedance(projectId, sceneNumber);
      } else if (options.useSubjectImage || projectDataHasReferenceImage(projectId)) {
        sceneData = await apiService.generateSceneFromSubject(projectId, sceneNumber, options);
      } else {
        sceneData = await apiService.generateScene(projectId, sceneNumber, options);
      }

      // API now includes refinements and refinementsCount - no extra GET needed!
      setSceneGenerationStatus((prev) => ({
        ...prev,
        [sceneNumber]: {
          ...sceneData,
          status: "generated",
          refinements: sceneData.refinements || [],
          refinementsCount: sceneData.refinementsCount || 0,
        },
      }));

      // Fetch updated project statistics after generation
      await fetchProjectData(projectId);

      return sceneData;
    } catch (error) {
      // Handle API errors
      console.error("Scene generation error:", error);

      let errorMessage = "An unexpected error occurred. Please try again.";
      let isRetryable = true;
      let retryAfter = null;
      let errorCode = null;

      if (error instanceof ApiError) {
        errorMessage = error.message;
        isRetryable = error.isRetryable;
        retryAfter = error.retryAfter;
        errorCode = error.errorCode;
      } else {
        errorMessage = error.message || errorMessage;
      }

      // Set error state
      setSceneGenerationStatus((prev) => ({
        ...prev,
        [sceneNumber]: {
          status: "failed",
          errorMessage,
          isRetryable,
          retryAfter,
          errorCode,
          refinements: [],
          refinementsCount: 0,
        },
      }));

      return null;
    } finally {
      setIsGeneratingScene(false);
    }
  };

  const projectDataHasReferenceImage = (projectId) => {
    if (projectData?.projectId !== projectId) return false;
    return Boolean(
      projectData.initialImageUrl ||
      projectData.logoUrl?.length ||
      projectData.productUrl?.length ||
      projectData.modelUrl?.length ||
      projectData.backgroundUrl?.length
    );
  };

  // Approve a scene
  const approveScene = async (
    projectId,
    sceneNumber,
    refinementIndex = null,
  ) => {
    try {
      const approvalData = await apiService.approveScene(
        projectId,
        sceneNumber,
        refinementIndex,
      );

      // Fetch updated project statistics after approval
      // This will update both projectData and sceneGenerationStatus
      await fetchProjectData(projectId);

      return approvalData;
    } catch (error) {
      console.error("Scene approval error:", error);
      throw error;
    }
  };

  // Fetch full project data with statistics
  const fetchProjectData = async (projectId) => {
    try {
      const fullProjectData = await apiService.getProject(projectId);

      // Update project data with full details including statistics
      setProjectData(fullProjectData);

      // Update scene generation status from the fetched data
      const updatedStatus = {};
      fullProjectData.scenes.forEach((scene) => {
        updatedStatus[scene.sceneNumber] = {
          ...scene,
          status: scene.status,
          videoUrl: scene.videoUrl,
          refinements: scene.refinements || [],
          refinementsCount: scene.refinementsCount || 0,
          errorMessage: scene.errorMessage,
        };
      });
      setSceneGenerationStatus(updatedStatus);

      return fullProjectData;
    } catch (error) {
      console.error("Failed to fetch project data:", error);
      throw error;
    }
  };

  // Refine a scene
  const refineScene = async (
    projectId,
    sceneNumber,
    visualFeedback,
    dialogue,
  ) => {
    setIsGeneratingScene(true);
    try {
      const refinedData = await apiService.refineScene(projectId, sceneNumber, {
        visualFeedback,
        dialogue,
      });

      // API now includes complete refinements array and count - no extra GET needed!
      setSceneGenerationStatus((prev) => ({
        ...prev,
        [sceneNumber]: {
          ...refinedData,
          status: "generated",
          refinements: refinedData.refinements || [],
          refinementsCount: refinedData.refinementsCount || 0,
        },
      }));

      // Fetch updated project statistics after refinement
      await fetchProjectData(projectId);

      return refinedData;
    } catch (error) {
      console.error("Scene refinement error:", error);
      throw error;
    } finally {
      setIsGeneratingScene(false);
    }
  };

  // Finalize and combine all scenes
  const finalizeProject = async (projectId, generateThumbnails) => {
    const useThumbnails = generateThumbnails !== undefined ? generateThumbnails : savedGenerateThumbnails;
    try {
      const finalData = await apiService.finalizeProject(projectId, useThumbnails, savedHookLine);

      // Fetch updated project statistics after finalization
      // This will get final stats including total cost, videos generated, etc.
      await fetchProjectData(projectId);

      return finalData;
    } catch (error) {
      console.error("Finalization error:", error);
      throw error;
    } 
  };

  // Create project with edited dialogues
  const createProjectWithScenes = async (editedScenes) => {
    try {
      // Get clientId and userId from URL
      const clientId = getClientIdFromUrl();
      const userId = getUserIdFromUrl();

      const projectData = await apiService.createProject(
        editedScenes,
        originalPrompt,
        clientId,
        userId,
        {
          aspectRatio: savedAspectRatio,
          sceneDuration: savedSceneDuration,
          logoUrl: savedLogoUrl,
          referenceImageUrl: savedReferenceImageUrl,
          referenceVideoUrl: savedReferenceVideoUrl,
          language: Array.isArray(savedLanguage) ? savedLanguage : savedLanguage ? [savedLanguage] : [],
         productImgUrls: savedProductImgUrls,
modelImgUrls: savedModelImgUrls,
backgroundUrls: savedBackgroundUrls,
          voiceSampleUrl: savedVoiceSampleUrl,
          isDirectMode: savedPersonalPrompt,
          aiMode: savedAiModel,
        },
      );

      // Store project data
      setProjectData(projectData);
      setCurrentSceneIndex(0);

      // Update the last message to show scene workflow
      // Replace dialogue editor message with scene workflow message
setConversations(prev =>
  prev.map((conv, i) =>
    i === currentConversation
      ? {
          ...conv,
          messages: conv.messages.map((msg, mi) =>
            mi === conv.messages.length - 1
              ? { text: '', type: 'bot', isSceneWorkflow: true, projectData, aiModel: savedAiModel }
              : msg
          ),
        }
      : conv
  )
);
    } catch (error) {
      console.error("Failed to create project:", error);
      throw error;
    } 
  };

  const handleNewChat = () => {
    setConversations([]);
  setCurrentConversation(0);
  setResetKey(prev => prev + 1); 
    setProjectData(null);
    setCurrentSceneIndex(0);
    setSceneGenerationStatus({});
    setOriginalPrompt("");
    setSavedAspectRatio(null);
    setSavedSceneDuration(null);
    setSavedLogoUrl(null);
    setSavedLanguage(null);
    setSavedProductImgUrls([]);
setSavedModelImgUrls([]);
setSavedBackgroundUrls([]);
    setSavedVoiceSampleUrl(null);
    setSavedAiModel('veo');
    savedAiModelRef.current = 'veo';
  };

  const handleResetProject = () => {
    // Reset project state but keep conversation history
    setProjectData(null);
    setCurrentSceneIndex(0);
    setSceneGenerationStatus({});
    setOriginalPrompt("");
    setSavedAspectRatio(null);
    setSavedSceneDuration(null);
    setSavedLogoUrl(null);
    setSavedLanguage(null);
    setSavedProductImgUrls([]);
setSavedModelImgUrls([]);
setSavedBackgroundUrls([]);
    setSavedVoiceSampleUrl(null);
    setSavedAiModel('veo');
    savedAiModelRef.current = 'veo';
  };

  const handleSelectConversation = (index) => {
    setCurrentConversation(index);
  };

  const currentMessages = conversations[currentConversation]?.messages || [];

  return {
    conversations,
    currentConversation,
    currentMessages,
    isLoading,
    handleSendMessage,
    handleNewChat,
    handleSelectConversation,
    handleResetProject,
    // New scene workflow functions
    projectData,
    currentSceneIndex,
    setCurrentSceneIndex,
    sceneGenerationStatus,
    isGeneratingScene,
    generateScene,
    approveScene,
    refineScene,
    finalizeProject,
    createProjectWithScenes,
    fetchProjectData,
    resetKey,
  };
};
